import * as React from "react";
import { request } from "../../../../services/cpln";
import { useAppSelector } from "../../../../store/hooks";
import { selectLabel, selectValue } from "../../../../store/timezoneSlice";
import { BaseChart } from "../../BaseChart";
import {
  QueryRangeResponse,
  ChartDataPoint,
  toRegex,
  processSeriesData,
  getTimeRange,
  formatCount,
  getMetricsErrorMessage,
  buildByClause,
  translateGrouping,
} from "../../utils";

export interface WorkloadRescheduledReplicasChartProps {
  org: string;
  workload?: string[];
  gvc?: string[];
  location?: string[];
  grouping?: string[];
  hideFromLabel?: string[];
  fromIso?: string | null;
  toIso?: string | null;
  step?: string;
}

export const WorkloadRescheduledReplicasChart: React.FC<WorkloadRescheduledReplicasChartProps> = ({
  org,
  workload = [],
  gvc = [],
  location = [],
  grouping = [],
  hideFromLabel = [],
  fromIso,
  toIso,
  step = "30s",
}) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [chartData, setChartData] = React.useState<ChartDataPoint[]>([]);
  const [seriesKeys, setSeriesKeys] = React.useState<string[]>([]);

  const timezoneValue = useAppSelector(selectValue);
  const timezoneLabel = useAppSelector(selectLabel);

  React.useEffect(() => {
    if (org) {
      fetchMetrics();
    }
  }, [org, workload, gvc, location, grouping, fromIso, toIso, step, timezoneValue]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const workloadRegex = toRegex(workload);
      const gvcRegex = toRegex(gvc);
      const locationRegex = toRegex(location);
      const translatedGrouping = translateGrouping(grouping, "workload");
      const byClause = buildByClause(translatedGrouping);
      const query = `sum(increase(workload_rescheduled_replicas{gvc=~"${gvcRegex}", workload=~"${workloadRegex}", location=~"${locationRegex}"}[15m])) ${byClause} or (sum(sum(cpu_billable{workload=~"${workloadRegex}", gvc=~"${gvcRegex}", location=~"${locationRegex}"}) ${byClause}) by (workload, gvc, location, version) * 0)`;

      const { start, end } = getTimeRange(fromIso, toIso);

      const response = await request<QueryRangeResponse>({
        service: "metrics",
        method: "post",
        url: `/metrics/org/${org}/api/v1/query_range?query=${encodeURIComponent(query)}&start=${start.toISOString()}&end=${end.toISOString()}&step=${step}`,
      });

      if (response.data.status !== "success") {
        setError((response.data as any).error || "Metrics API returned an error");
        return;
      }
      const result = response.data.data?.result || [];
      const { data: processedData, keys } = processSeriesData(result, {
        grouping: translatedGrouping,
        timezoneValue,
        hideFromLabel,
      });
      setChartData(processedData);
      setSeriesKeys(keys);
    } catch (err: any) {
      console.error("Failed to fetch metrics:", err);
      setError(getMetricsErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseChart
      title="Workload Rescheduled Replicas"
      loading={loading}
      error={error}
      chartData={chartData}
      seriesKeys={seriesKeys}
      formatYAxis={formatCount}
      formatTooltip={(value) => value.toFixed(0)}
      timezoneLabel={timezoneLabel}
    />
  );
};
